/**
 * Responsibility: Build a renderer-agnostic atlas view model and simple viewport projection.
 * Scope: Owns atlas UI data normalization only; rendering stays with the caller.
 */
import type { Atlas, AtlasNode, DormantCondition, GameState } from "@anyquest/shared-core"
import { atlasNeighborsFast, buildAdjacencyList, buildAtlasComponentReport, AdjacencyList } from '../../engine/atlas-graph'

export interface AtlasViewNode {
  id: string
  gridX: number
  gridY: number
  gridZ: number
  name: string
  desc?: string
  items: string[]
  flags: Record<string, string | number | boolean | null>
  dormantFlags: DormantCondition[]
  visionUrl?: string
  isCurrent: boolean
  hasRemoteAccess: boolean
  degree: number
  distanceFromCurrent: number
  directionFromCurrent: string
  isFrontier: boolean
  kind: 'room' | 'passage'
  axis: 'horizontal' | 'vertical' | 'radial'
  summary: string
}

export interface AtlasViewEdge {
  id: string
  sourceId: string
  targetId: string
}

export interface AtlasViewModel {
  currentNodeId: string
  frontierCount: number
  routeCount: number
  roomCount: number
  suggestedExpansion:
    | {
        fromKey: string
        toKey: string
        direction: string
      }
    | null
  bounds: {
    minGridX: number
    maxGridX: number
    minGridY: number
    maxGridY: number
    minGridZ: number
    maxGridZ: number
  }
  nodes: AtlasViewNode[]
  edges: AtlasViewEdge[]
}

export interface AtlasProjectionNode extends AtlasViewNode {
  screenX: number
  screenY: number
  shortLabel: string
}

export interface AtlasProjectionEdge extends AtlasViewEdge {
  source: AtlasProjectionNode
  target: AtlasProjectionNode
}

export interface AtlasProjection {
  currentNodeId: string
  nodes: AtlasProjectionNode[]
  edges: AtlasProjectionEdge[]
}

function readFlags(node: AtlasNode | undefined): Record<string, string | number | boolean | null> {
  return { ...(node?.flags ?? {}) }
}

function directionLabel(fromX: number, fromY: number, fromZ: number, toX: number, toY: number, toZ: number): string {
  if (toZ === fromZ) {
    if (toX === fromX && toY === fromY + 1) return 'north'
    if (toX === fromX + 1 && toY === fromY) return 'east'
    if (toX === fromX && toY === fromY - 1) return 'south'
    if (toX === fromX - 1 && toY === fromY) return 'west'
  } else {
    if (toX === fromX && toY === fromY && toZ === fromZ + 1) return 'up'
    if (toX === fromX && toY === fromY && toZ === fromZ - 1) return 'down'
  }
  return 'remote'
}

function sentenceCase(value: string) {
  const text = String(value ?? '').trim()
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : 'Unknown'
}

function inferNodeKind(node: AtlasNode | undefined, degree: number): 'room' | 'passage' {
  const presentation = String(node?.flags?.presentation ?? '').trim().toLowerCase()
  if (presentation === 'passage') return 'passage'
  if (presentation === 'room') return 'room'
  const name = String(node?.name ?? '').toLowerCase()
  if (/\b(passage|approach|span|way|corridor|hallway|causeway|bridge|walk|gate path|route)\b/.test(name)) return 'passage'
  if (degree <= 2 && /\b(outer|approach|passage|span|way)\b/.test(name)) return 'passage'
  return 'room'
}

function inferNodeAxis(adj: AdjacencyList, key: string): 'horizontal' | 'vertical' | 'radial' {
  const neighbors = atlasNeighborsFast(adj, key)
  const hasHorizontal = neighbors.some((entry) => entry.direction === 'east' || entry.direction === 'west')
  const hasVertical = neighbors.some((entry) => entry.direction === 'north' || entry.direction === 'south')
  if (hasHorizontal && !hasVertical) return 'horizontal'
  if (hasVertical && !hasHorizontal) return 'vertical'
  return 'radial'
}

export function atlasNodeHasRemoteAccess(node: Pick<AtlasViewNode, 'flags'> | AtlasNode | undefined): boolean {
  const flags = readFlags(node as AtlasNode | undefined)
  return !!(
    flags.remote_access === true
    || flags.remoteAccess === true
    || flags.allow_remote_interaction === true
    || flags.allowRemoteInteraction === true
  )
}

export function shortenAtlasLabel(name: string, max = 12): string {
  const text = String(name ?? '').trim()
  if (!text) return 'Unknown'
  return text.length > max ? `${text.slice(0, Math.max(1, max - 1))}…` : text
}

export function buildAtlasViewModel(atlas: Atlas | null | undefined, location: GameState['player']['location'] | null | undefined): AtlasViewModel {
  const lx = location?.x ?? 0, ly = location?.y ?? 0, lz = location?.z ?? 0
  const currentNodeId = `${lx}_${ly}_${lz}`
  const adj = buildAdjacencyList(atlas)
  const report = buildAtlasComponentReport(atlas, currentNodeId)
  const entries = Object.entries(atlas?.nodes ?? {})
  
  const nodes = entries.map(([id, node]) => {
    const [gridX, gridY, gridZ] = id.split('_').map((value) => Number.parseInt(value, 10) || 0)
    const degree = report.degrees[id] ?? 0
    const isCurrent = id === currentNodeId
    const directionFromCurrent = directionLabel(lx, ly, lz, gridX, gridY, gridZ)
    const kind = isCurrent ? 'room' : inferNodeKind(node, degree)
    return {
      id,
      gridX,
      gridY,
      gridZ,
      name: String(node?.name ?? id),
      desc: node?.desc,
      items: Array.isArray(node?.items) ? [...node.items] : [],
      flags: readFlags(node),
      dormantFlags: Array.isArray(node?.dormantFlags) ? [...node.dormantFlags] : [],
      visionUrl: node?.visionUrl,
      isCurrent,
      hasRemoteAccess: atlasNodeHasRemoteAccess(node),
      degree,
      distanceFromCurrent: report.distances[id] ?? 0,
      directionFromCurrent: isCurrent ? 'current' : directionFromCurrent,
      isFrontier: report.frontierKeys.includes(id),
      kind,
      axis: inferNodeAxis(adj, id),
      summary: isCurrent
        ? 'You are here now.'
        : kind === 'passage'
          ? `${sentenceCase(directionFromCurrent)} passage · ${degree <= 1 ? 'frontier link' : 'through-route'}`
          : `${sentenceCase(directionFromCurrent)} room · ${degree <= 1 ? 'edge room' : `${degree} links`}`,
    }
  })

  const nodeIds = new Set(nodes.map((node) => node.id))
  const edges = (atlas?.edges ?? [])
    .map((edge) => {
      const sourceId = `${edge.x1}_${edge.y1}_${edge.z1 ?? 0}`
      const targetId = `${edge.x2}_${edge.y2}_${edge.z2 ?? 0}`
      if (!nodeIds.has(sourceId) || !nodeIds.has(targetId)) return null
      const [first, second] = sourceId < targetId ? [sourceId, targetId] : [targetId, sourceId]
      return {
        id: `${first}->${second}`,
        sourceId,
        targetId,
      }
    })
    .filter(Boolean) as AtlasViewEdge[]

  const gridXs = nodes.map((node) => node.gridX)
  const gridYs = nodes.map((node) => node.gridY)
  const gridZs = nodes.map((node) => node.gridZ)

  return {
    currentNodeId,
    frontierCount: nodes.filter((node) => node.isFrontier && !node.isCurrent).length,
    routeCount: nodes.filter((node) => node.kind === 'passage').length,
    roomCount: nodes.filter((node) => node.kind === 'room').length,
    suggestedExpansion: report.suggestedExpansion,
    bounds: {
      minGridX: gridXs.length ? Math.min(...gridXs) : 0,
      maxGridX: gridXs.length ? Math.max(...gridXs) : 0,
      minGridY: gridYs.length ? Math.min(...gridYs) : 0,
      maxGridY: gridYs.length ? Math.max(...gridYs) : 0,
      minGridZ: gridZs.length ? Math.min(...gridZs) : 0,
      maxGridZ: gridZs.length ? Math.max(...gridZs) : 0,
    },
    nodes,
    edges,
  }
}

export function projectAtlasViewModel(
  model: AtlasViewModel,
  viewport: { width: number; height: number },
  scaleFactor = 86,
): AtlasProjection {
  const spanX = Math.max(1, model.bounds.maxGridX - model.bounds.minGridX)
  const spanY = Math.max(1, model.bounds.maxGridY - model.bounds.minGridY)
  const padding = Math.min(96, Math.max(42, Math.min(viewport.width, viewport.height) * 0.14))
  const step = Math.max(
    42,
    Math.min(
      scaleFactor,
      (viewport.width - (padding * 2)) / spanX,
      (viewport.height - (padding * 2)) / spanY,
    ),
  )
  const offsetX = spanX === 0
    ? viewport.width / 2
    : (viewport.width - (spanX * step)) / 2
  const offsetY = spanY === 0
    ? viewport.height / 2
    : (viewport.height - (spanY * step)) / 2
  const nodes = model.nodes.map((node) => ({
    ...node,
    screenX: spanX === 0
      ? viewport.width / 2
      : offsetX + ((node.gridX - model.bounds.minGridX) * step) + (node.gridZ * (step * 0.15)),
    screenY: spanY === 0
      ? viewport.height / 2
      : offsetY + ((model.bounds.maxGridY - node.gridY) * step) - (node.gridZ * (step * 0.15)),
    shortLabel: node.kind === 'passage' && node.directionFromCurrent !== 'remote'
      ? sentenceCase(node.directionFromCurrent)
      : shortenAtlasLabel(node.name),
  }))
  const byId = new Map(nodes.map((node) => [node.id, node]))
  const edges = model.edges
    .map((edge) => {
      const source = byId.get(edge.sourceId)
      const target = byId.get(edge.targetId)
      if (!source || !target) return null
      return { ...edge, source, target }
    })
    .filter(Boolean) as AtlasProjectionEdge[]

  return {
    currentNodeId: model.currentNodeId,
    nodes,
    edges,
  }
}
